/* Nividas PDP colorway strip — May 2026.
   Desktop: click-and-drag to scroll, plus center-the-active-swatch on load.
   Mobile:  native touch scrolling owns the gesture entirely; this script
            doesn't touch touch events. */

(function () {
  'use strict';

  // Centering math uses getBoundingClientRect, not offsetLeft, because the
  // strip is `position: static` and offsetLeft would be relative to <body>
  // (or some other ancestor), not the strip — which silently produced a
  // wrong target every time. Rects are viewport-relative and don't care
  // about the offsetParent chain. Uses scrollTo with `smooth` behavior so
  // the strip animates into place when the page loads with a new active
  // color, rather than snapping abruptly.
  function centerActive(strip) {
    const active = strip.querySelector('.nv-pdp-colorway__item.is-active');
    if (!active) return;
    const stripRect = strip.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const delta = (activeRect.left + activeRect.width / 2) - (stripRect.left + stripRect.width / 2);
    const max = strip.scrollWidth - strip.clientWidth;
    const target = Math.max(0, Math.min(max, strip.scrollLeft + delta));
    strip.scrollTo({ left: target, behavior: 'smooth' });
  }

  // Center the active swatch on load. Runs immediately, then again after
  // each remaining image's load event because final layout can shift a few
  // pixels once dimensions settle. Width/height attrs on the <img> reserve
  // space so the first pass is usually already accurate.
  function centerWhenReady(strip) {
    centerActive(strip);
    const imgs = Array.from(strip.querySelectorAll('img'));
    let pending = imgs.filter((img) => !img.complete).length;
    if (pending === 0) return;
    imgs.forEach((img) => {
      if (img.complete) return;
      const done = () => {
        pending--;
        if (pending === 0) centerActive(strip);
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  // Desktop click-and-drag to scroll. Mouse events only — they don't fire
  // for touch input, so this can't interfere with native mobile scrolling.
  // Document-level move/up listeners are attached on demand so the drag
  // survives the cursor leaving the strip bounds.
  function attachDragScroll(strip) {
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    let savedScrollBehavior = '';

    function onMove(e) {
      const dx = e.pageX - startX;
      moved = Math.max(moved, Math.abs(dx));
      if (moved > 4) {
        // Prevent text selection / image drag while panning.
        e.preventDefault();
        strip.scrollLeft = startScroll - dx;
      }
    }

    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      strip.style.scrollBehavior = savedScrollBehavior;
    }

    strip.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      startX = e.pageX;
      startScroll = strip.scrollLeft;
      moved = 0;
      // CSS sets `scroll-behavior: smooth` on the strip, which would
      // animate every scrollLeft mutation during drag and produce a
      // laggy, "rubbery" feel. Force instant scroll for the drag, then
      // restore on mouseup.
      savedScrollBehavior = strip.style.scrollBehavior;
      strip.style.scrollBehavior = 'auto';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    // Suppress click if the user dragged past the threshold, so they
    // don't accidentally navigate to a sibling product after panning.
    strip.addEventListener('click', (e) => {
      if (moved > 6) {
        e.preventDefault();
        e.stopPropagation();
        moved = 0;
      }
    }, true);

    // Block native HTML5 image drag — without this, mousedown on a
    // swatch <img> can start a "drag image to desktop" gesture that
    // hijacks our scroll-drag mid-motion.
    strip.addEventListener('dragstart', (e) => e.preventDefault());
  }

  function init(strip) {
    if (!strip || strip.dataset.nvBound === '1') return;
    strip.dataset.nvBound = '1';
    attachDragScroll(strip);
    centerWhenReady(strip);
  }

  function initAll() {
    document.querySelectorAll('[data-nv-pdp-colorway]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
